import { Controller } from "@hotwired/stimulus";

// Client-side validation for forms built with ValidatedFormBuilder. Each field
// target wraps one question and has an id; its inline error is the element
// with id "<field id>_error".
//
//   data-required="true"             always required
//   data-required-if-checked="<id>"  required only while that radio/checkbox is checked,
//                                    and disabled (with its selection cleared) otherwise
//   data-required-message="..."      shown when a required field is empty
//   data-pattern-message="..."       shown when an input fails its `pattern`
//
// Format rules are the inputs' native `pattern`/`type` attributes, checked
// through the browser's validity API. The form should have `novalidate` so the
// browser's own error bubbles don't appear.
export default class extends Controller {
  static targets = ["field", "summary", "summaryList"];

  // fields the user has changed, so clearing one is flagged on blur
  #edited = new WeakSet();

  // server-rendered summary items with no field on the page (e.g. :base); the
  // client can't re-check them, so they stay listed until the next submit
  #serverOnlyItems = [];

  connect() {
    this.#syncConditionalFields();
    if (!this.hasSummaryTarget || this.summaryTarget.hidden) return;

    const fieldIds = new Set(this.fieldTargets.map((field) => field.id));
    this.#serverOnlyItems = [...this.summaryListTarget.children].filter(
      (item) => !fieldIds.has(item.querySelector("a")?.hash.slice(1))
    );

    this.fieldTargets
      .filter((field) => this.#showingError(field))
      .forEach((field) => this.#setError(field, this.#errorElement(field).textContent));

    this.summaryTarget.scrollIntoView({ block: "start" });
    this.summaryTarget.focus({ preventScroll: true });
  }

  blur(event) {
    this.#fieldsContaining(event.target).forEach((field) => {
      const edited = this.#edited.has(field);
      if (edited || this.#value(field) !== "" || this.#showingError(field)) this.#validate(field, { flagEmpty: edited });
    });
    this.#refreshSummary();
  }

  recheck(event) {
    this.#syncConditionalFields();
    this.#fieldsContaining(event.target).forEach((field) => this.#edited.add(field));
    this.fieldTargets.filter((field) => this.#showingError(field)).forEach((field) => this.#validate(field));
    this.#refreshSummary();
  }

  submit(event) {
    const errors = this.fieldTargets
      .map((field) => ({ field, message: this.#validate(field, { flagEmpty: true }) }))
      .filter(({ message }) => message);

    if (errors.length === 0) return;

    event.preventDefault();
    event.stopPropagation();
    this.#showSummary(errors);
  }

  focusField(event) {
    const field = document.getElementById(event.currentTarget.hash.slice(1));
    if (!field) return;

    event.preventDefault();
    this.#focus(field);
  }

  #focus(field) {
    field.scrollIntoView({ block: "center" });
    field.querySelector("input:not([type=hidden]), select, textarea, canvas")?.focus({ preventScroll: true });
  }

  #validate(field, { flagEmpty = false } = {}) {
    const value = this.#value(field);
    let message = null;

    if (value === "") {
      if (this.#isRequired(field) && (flagEmpty || this.#showingError(field))) message = field.dataset.requiredMessage;
    } else {
      message = this.#formatMessage(field) || this.#onlyOneMessage(field);
    }

    this.#setError(field, message);
    return message;
  }

  #onlyOneMessage(field) {
    const { onlyOneMessage } = field.dataset;
    if (!onlyOneMessage) return null;

    const filled = this.fieldTargets.filter((nested) => nested !== field && field.contains(nested) && this.#value(nested) !== "");
    return filled.length > 1 ? onlyOneMessage : null;
  }

  #value(field) {
    const inputs = [...field.querySelectorAll("input, select, textarea")];

    if (inputs.some((input) => input.type === "radio" || input.type === "checkbox")) {
      return inputs.some((input) => input.checked) ? "checked" : "";
    }

    return inputs.map((input) => input.value.trim()).filter(Boolean).join(" ");
  }

  #formatMessage(field) {
    const invalid = this.#ownInputs(field).find((input) => input.validity.patternMismatch || input.validity.typeMismatch);
    if (!invalid) return null;

    return field.dataset.patternMessage || invalid.validationMessage;
  }

  #syncConditionalFields() {
    this.fieldTargets
      .filter((field) => field.dataset.requiredIfChecked)
      .forEach((field) => {
        const enabled = this.#isRequired(field);

        this.#ownInputs(field).forEach((input) => {
          input.disabled = !enabled;
          if (!enabled) input.checked = false;
        });
        if (!enabled) this.#setError(field, null);
      });
  }

  #isRequired(field) {
    const { required, requiredIfChecked } = field.dataset;
    if (requiredIfChecked) return !!document.getElementById(requiredIfChecked)?.checked;

    return required === "true";
  }

  #showingError(field) {
    return !this.#errorElement(field).hidden;
  }

  #errorElement(field) {
    return document.getElementById(`${field.id}_error`);
  }

  #setError(field, message) {
    const error = this.#errorElement(field);
    error.textContent = message || "";
    error.hidden = !message;
    field.classList.toggle("has-error", !!message);

    this.#ownInputs(field).forEach((input) => {
      if (message) {
        input.setAttribute("aria-invalid", "true");
      } else {
        input.removeAttribute("aria-invalid");
        input.classList.remove("field-with-error");
      }
    });
  }

  #ownInputs(field) {
    return [...field.querySelectorAll("input, select, textarea")].filter(
      (input) => input.closest("[data-form-validation-target~='field']") === field
    );
  }

  #fieldsContaining(element) {
    return this.fieldTargets.filter((field) => field.contains(element));
  }
  #refreshSummary() {
    if (!this.hasSummaryTarget || this.summaryTarget.hidden) return;

    const errors = this.fieldTargets
      .filter((field) => this.#showingError(field))
      .map((field) => ({ field, message: this.#errorElement(field).textContent }));

    if (errors.length === 0 && this.#serverOnlyItems.length === 0) {
      this.summaryTarget.hidden = true;
      return;
    }

    this.#renderSummaryList(errors);
  }

  #showSummary(errors) {
    if (!this.hasSummaryTarget) {
      this.#focus(errors[0].field);
      return;
    }

    this.#renderSummaryList(errors);
    this.summaryTarget.hidden = false;
    this.summaryTarget.scrollIntoView({ block: "start" });
    this.summaryTarget.focus({ preventScroll: true });
  }


  #renderSummaryList(errors) {
    const list = this.summaryListTarget;
    const items = [...this.#serverOnlyItems, ...errors.map((error) => this.#summaryItem(error))];

    [...list.children].filter((item) => !items.includes(item)).forEach((item) => item.remove());
    items.forEach((item, index) => {
      if (list.children[index] !== item) list.insertBefore(item, list.children[index] ?? null);
    });
  }

  #summaryItem({ field, message }) {
    const existing = [...this.summaryListTarget.children].find((item) => item.querySelector("a")?.hash === `#${field.id}`);
    if (existing) {
      const link = existing.querySelector("a");
      if (link.textContent !== message) link.textContent = message;
      return existing;
    }

    const link = document.createElement("a");
    link.href = `#${field.id}`;
    link.textContent = message;
    link.dataset.action = "form-validation#focusField";

    const item = document.createElement("li");
    item.append(link);
    return item;
  }
}
